import { ActionIcon, Button, Group, Select, Stack, Table, Text, TextInput, Tooltip } from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { VAT_TREATMENTS, type VatTreatment } from '@zebra/core';
import { ProductSelect } from './ProductSelect';
import { LocationSelect } from './LocationSelect';
import { qtyInputLabel } from '../format';
import type { ProductRow } from '../types';

/** One dispatch line the user is editing on a DRAFT dispatch's detail page.
 * `key` is a client-only React key (never sent to the API — the server assigns
 * `lineNo` on create/update); `productLabel`/`locationLabel` are denormalized
 * alongside their ids purely so the review margin preview can render names
 * without a second round of fetches (mirrors features/stores-transfers/
 * components/LineRowsEditor.tsx's `LineFormRow`). */
export interface DispatchLineFormRow {
  key: string;
  productId: string;
  productLabel: string;
  fromLocationId: string;
  locationLabel: string;
  qty: string;
  priceRand: string;
  vatTreatment: VatTreatment;
}

export function emptyDispatchLineRow(): DispatchLineFormRow {
  return {
    key: crypto.randomUUID(),
    productId: '',
    productLabel: '',
    fromLocationId: '',
    locationLabel: '',
    qty: '',
    priceRand: '',
    vatTreatment: 'STANDARD',
  };
}

/** True only when every line has a product, a from-location, a positive
 * quantity (a whole number for an EACH product — echoes the server's own
 * `assertEachLinesWholeUnits` guard in modules/sales/dispatches.ts, which
 * remains the actual enforcement point at create/PATCH/post), and a price.
 * Mirrors DispatchManualLinesEditor.tsx's `dispatchManualLinesValid` (wave-gate
 * fix: this shared editor previously let an EACH qty be retyped to "12.500"
 * and PATCHed through with only the server's zod shape check to stop it). */
export function dispatchLinesValid(rows: DispatchLineFormRow[], productsById: Map<string, ProductRow>): boolean {
  if (rows.length === 0) return false;
  return rows.every((row) => {
    if (!row.productId || !row.fromLocationId || !row.priceRand.trim()) return false;
    const qty = Number(row.qty);
    if (!row.qty.trim() || !Number.isFinite(qty) || qty <= 0) return false;
    if (productsById.get(row.productId)?.baseUom === 'EACH' && !Number.isInteger(qty)) return false;
    return true;
  });
}

interface DispatchLinesEditorProps {
  rows: DispatchLineFormRow[];
  onChange: (rows: DispatchLineFormRow[]) => void;
  siteId: string | undefined;
  /** ALL products (active + inactive), keyed by id — resolves each row's
   * baseUom for the unit-aware qty placeholder, including a since-deactivated
   * product already on the dispatch (same precedent as
   * SalesOrderLinesEditor.tsx's identical prop). */
  productsById: Map<string, ProductRow>;
}

/** Repeatable product/from-location/qty/price/VAT line rows for a DRAFT
 * dispatch's detail-page editor (BUILD_SPEC.md §5.7 dispatch_lines: many lines per
 * dispatch). The guided "Dispatch a load" wizard (DispatchWizard.tsx) is the fast,
 * single-line-per-ticket path for the common case; this editor is the power-user
 * fallback for adding/adjusting lines on an already-created DRAFT before Post. */
export function DispatchLinesEditor({ rows, onChange, siteId, productsById }: DispatchLinesEditorProps) {
  function updateRow(key: string, patch: Partial<DispatchLineFormRow>) {
    onChange(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  }

  function removeRow(key: string) {
    onChange(rows.filter((row) => row.key !== key));
  }

  function addRow() {
    onChange([...rows, emptyDispatchLineRow()]);
  }

  return (
    <Stack gap="xs">
      <Table.ScrollContainer minWidth={640}>
        <Table verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Product</Table.Th>
              <Table.Th>From location</Table.Th>
              {/* Neutral header — a dispatch can mix KG and EACH products, so
                  each row's own placeholder ("kg" / "Units") carries the unit
                  instead of a single table-wide header that would lie for one
                  of them (same precedent as SalesOrderLinesEditor.tsx). */}
              <Table.Th w={120}>
                <Tooltip label="In the product's base unit — kg or count, depending on the product chosen." multiline w={220}>
                  <span>Qty</span>
                </Tooltip>
              </Table.Th>
              <Table.Th w={140}>Price / unit (R, ex VAT)</Table.Th>
              <Table.Th w={140}>VAT</Table.Th>
              <Table.Th w={48} />
            </Table.Tr>
          </Table.Thead>
        <Table.Tbody>
          {rows.map((row) => {
            const baseUom = productsById.get(row.productId)?.baseUom;
            return (
              <Table.Tr key={row.key}>
                <Table.Td>
                  <ProductSelect
                    value={row.productId}
                    onChange={(productId, productLabel) => updateRow(row.key, { productId, productLabel })}
                    aria-label="Product"
                  />
                </Table.Td>
                <Table.Td>
                  <LocationSelect
                    siteId={siteId}
                    value={row.fromLocationId}
                    onChange={(fromLocationId, locationLabel) => updateRow(row.key, { fromLocationId, locationLabel })}
                    aria-label="From location"
                  />
                </Table.Td>
                <Table.Td>
                  <TextInput
                    aria-label="Quantity"
                    placeholder={qtyInputLabel(baseUom, 'kg', 'Units')}
                    value={row.qty}
                    onChange={(e) => updateRow(row.key, { qty: e.currentTarget.value })}
                  />
                </Table.Td>
                <Table.Td>
                  <TextInput
                    aria-label="Price per unit"
                    placeholder="0.00"
                    value={row.priceRand}
                    onChange={(e) => updateRow(row.key, { priceRand: e.currentTarget.value })}
                  />
                </Table.Td>
                <Table.Td>
                  <Select
                    aria-label="VAT treatment"
                    data={VAT_TREATMENTS.map((v) => ({ value: v, label: v }))}
                    value={row.vatTreatment}
                    onChange={(v) => updateRow(row.key, { vatTreatment: (v as VatTreatment) ?? 'STANDARD' })}
                    comboboxProps={{ withinPortal: true }}
                  />
                </Table.Td>
                <Table.Td>
                  <Tooltip label="Remove this line from the dispatch">
                    <ActionIcon color="red" variant="subtle" aria-label="Remove line" onClick={() => removeRow(row.key)} disabled={rows.length <= 1}>
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      <Group justify="space-between">
        <Button variant="light" leftSection={<IconPlus size={16} />} onClick={addRow}>
          Add line
        </Button>
        {rows.length === 0 && (
          <Text size="sm" c="dimmed">
            At least one line is required.
          </Text>
        )}
      </Group>
    </Stack>
  );
}
